import { useState, useEffect } from 'react';
import useAuthStore from '../stores/authStore';
import { BackendAPI } from '../lib/api';
import { UserPreferences, ScopeConfig, ScopeHeading, GenerationPreferences } from '../types';

import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { Input } from '../components/ui/Input';
import { Loader2, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';

// ================================================================= //
//          Settings Page (formerly "Profile")                       //
// ================================================================= //
// Account info + scope-generation defaults + generation preferences.//
// Resilient by design: if preferences fail to load we fall back to  //
// a client-side default set so the page always renders (no more     //
// "Not Found" dead-end).                                            //
// ================================================================= //

const DAMAGE_TYPES = ['water', 'fire', 'wind', 'hurricane', 'hail', 'flood', 'other'];

const CLIENT_DEFAULT_PREFS: UserPreferences = {
  scope_config: {
    structure_type: 'top_down',
    headings: [],
    damage_type_mapping: { flood: 'bottom_up', water: 'bottom_up' },
  },
  generation: {
    default_damage_type: null,
    default_jurisdiction: null,
    helpful_mode: false,
    cat3_default: false,
  },
};

const selectClass =
  'flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export default function ProfilePage() {
  const { user, roles } = useAuthStore();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingScope, setSavingScope] = useState(false);
  const [savingGen, setSavingGen] = useState(false);
  // Non-fatal load warning — the page still renders with defaults.
  const [loadWarning, setLoadWarning] = useState<string | null>(null);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const prefs = await BackendAPI.getUserPreferences();
        setPreferences({
          ...prefs,
          generation: prefs.generation ?? CLIENT_DEFAULT_PREFS.generation,
        });
      } catch (err) {
        // Resilient fallback: render defaults rather than a dead error page.
        setLoadWarning(
          'Could not load saved settings — showing defaults. Saving will create them.',
        );
        setPreferences(CLIENT_DEFAULT_PREFS);
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, []);

  const updateScope = (patch: Partial<ScopeConfig>) => {
    setPreferences((prev) =>
      prev ? { ...prev, scope_config: { ...prev.scope_config, ...patch } } : prev,
    );
  };

  const updateGeneration = (patch: Partial<GenerationPreferences>) => {
    setPreferences((prev) =>
      prev
        ? {
            ...prev,
            generation: { ...(prev.generation ?? CLIENT_DEFAULT_PREFS.generation!), ...patch },
          }
        : prev,
    );
  };

  const setHeadings = (headings: ScopeHeading[]) => {
    // Re-number order to match array position so the backend persists the
    // intended sequence.
    updateScope({ headings: headings.map((h, i) => ({ ...h, order: i })) });
  };

  const moveHeading = (index: number, dir: -1 | 1) => {
    if (!preferences) return;
    const list = [...preferences.scope_config.headings];
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setHeadings(list);
  };

  const handleSaveScope = async () => {
    if (!preferences) return;
    setSavingScope(true);
    try {
      const updated = await BackendAPI.updateScopeConfig(preferences.scope_config);
      setPreferences((prev) => (prev ? { ...prev, scope_config: updated.scope_config } : prev));
      setLoadWarning(null);
    } catch (err) {
      alert(`Error saving scope settings: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setSavingScope(false);
    }
  };

  const handleSaveGeneration = async () => {
    if (!preferences?.generation) return;
    setSavingGen(true);
    try {
      const updated = await BackendAPI.updateGenerationPrefs(preferences.generation);
      setPreferences((prev) => (prev ? { ...prev, generation: updated.generation } : prev));
      setLoadWarning(null);
    } catch (err) {
      alert(`Error saving generation settings: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setSavingGen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const prefs = preferences ?? CLIENT_DEFAULT_PREFS;
  const generation = prefs.generation ?? CLIENT_DEFAULT_PREFS.generation!;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-primary">Settings</h1>
        <p className="mt-1 text-muted-foreground">
          Manage your account and how scopes are generated.
        </p>
      </div>

      {loadWarning && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{loadWarning}</span>
        </div>
      )}

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
          <CardDescription>Linked to your authentication provider.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-4">
            <img
              src={
                user?.photoURL ||
                `https://ui-avatars.com/api/?name=${user?.displayName || user?.email}&background=10b981&color=fff`
              }
              alt="User Avatar"
              className="h-20 w-20 rounded-full"
            />
            <div>
              <p className="text-lg font-semibold">{user?.displayName || 'User'}</p>
              <p className="text-muted-foreground">{user?.email}</p>
              {roles.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {roles.map((r) => (
                    <span
                      key={r}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scope Generation Defaults */}
      <Card>
        <CardHeader>
          <CardTitle>Scope Structure</CardTitle>
          <CardDescription>
            How generated scopes are organized. Can be overridden per project or carrier.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Default structure</Label>
            <select
              value={prefs.scope_config.structure_type}
              onChange={(e) => updateScope({ structure_type: e.target.value as ScopeConfig['structure_type'] })}
              className={selectClass}
            >
              <option value="top_down">Top-Down (Standard)</option>
              <option value="bottom_up">Bottom-Up (for Flood Claims)</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Choose "Bottom-Up" for flood claims to start from the floor.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Headings</Label>
            <p className="text-xs text-muted-foreground">
              Reorder, rename, or disable the headings used to group line items.
            </p>
            <div className="divide-y rounded-md border">
              {prefs.scope_config.headings.length === 0 && (
                <p className="px-3 py-4 text-sm text-muted-foreground">No headings configured.</p>
              )}
              {prefs.scope_config.headings.map((h, i) => (
                <div key={h.id} className="flex items-center gap-2 px-3 py-2">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => moveHeading(i, -1)}
                      disabled={i === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label={`Move ${h.name} up`}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveHeading(i, 1)}
                      disabled={i === prefs.scope_config.headings.length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label={`Move ${h.name} down`}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  <Input
                    value={h.name}
                    onChange={(e) => {
                      const list = [...prefs.scope_config.headings];
                      list[i] = { ...h, name: e.target.value };
                      setHeadings(list);
                    }}
                    className="flex-1"
                  />
                  <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={h.enabled}
                      onChange={(e) => {
                        const list = [...prefs.scope_config.headings];
                        list[i] = { ...h, enabled: e.target.checked };
                        setHeadings(list);
                      }}
                    />
                    Enabled
                  </label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveScope} isLoading={savingScope}>
            Save structure
          </Button>
        </CardFooter>
      </Card>

      {/* Generation Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Generation Defaults</CardTitle>
          <CardDescription>Pre-filled when you start a new scope or chat.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Default damage type</Label>
              <select
                value={generation.default_damage_type ?? ''}
                onChange={(e) => updateGeneration({ default_damage_type: e.target.value || null })}
                className={selectClass}
              >
                <option value="">No default</option>
                {DAMAGE_TYPES.map((d) => (
                  <option key={d} value={d}>
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="gen-zip">Default jurisdiction (ZIP)</Label>
              <Input
                id="gen-zip"
                value={generation.default_jurisdiction ?? ''}
                onChange={(e) => updateGeneration({ default_jurisdiction: e.target.value || null })}
                placeholder="e.g. 33602"
                maxLength={10}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={generation.helpful_mode}
              onChange={(e) => updateGeneration({ helpful_mode: e.target.checked })}
            />
            <span>
              Helpful mode
              <span className="block text-xs text-muted-foreground">
                Add inline annotations explaining each generated line item.
              </span>
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={generation.cat3_default}
              onChange={(e) => updateGeneration({ cat3_default: e.target.checked })}
            />
            <span>
              Cat-3 water by default
              <span className="block text-xs text-muted-foreground">
                Start new scopes with the Category-3 water override on.
              </span>
            </span>
          </label>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveGeneration} isLoading={savingGen}>
            Save defaults
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
